import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { startPoll } from "@/lib/poll";
import { timeAgo, formatApiError, limitWords, countWords } from "@/lib/format";
import { useProfileViewer } from "@/components/Layout";
import { ExpandableText } from "@/components/ExpandableText";
import { toast } from "sonner";

const field = { background: "var(--bg-primary)", border: "1px solid var(--border)", color: "var(--text-primary)" };

function ResponseReply({ r }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/responses/${r.id}/reply`, { text });
      toast.success("Chat started");
      navigate(`/chat?c=${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  if (r.conversation_id)
    return <button onClick={() => navigate(`/chat?c=${r.conversation_id}`)} data-testid={`myask-open-chat-${r.id}`} className="btn text-xs tracking-widest px-4 py-2">OPEN CHAT →</button>;
  if (!open)
    return <button onClick={() => setOpen(true)} data-testid={`myask-reply-btn-${r.id}`} className="btn text-xs tracking-widest px-4 py-2">REPLY</button>;
  return (
    <div className="flex flex-col gap-3">
      <textarea data-testid={`myask-reply-input-${r.id}`} value={text} onChange={(e) => setText(limitWords(e.target.value, text, 1000))} rows={3} placeholder="reply to this answer to start the chat…" className="w-full p-3 text-base resize-none" style={field} />
      <div className="text-right -mt-1 text-xs tracking-widest" style={{ color: "var(--text-muted)" }}>{countWords(text).toLocaleString()} / 1,000 words</div>
      <div className="flex gap-3">
        <button onClick={submit} disabled={busy} data-testid={`myask-reply-submit-${r.id}`} className="btn text-xs tracking-widest px-4 py-2">{busy ? "…" : "REPLY & START CHAT"}</button>
        <button onClick={() => { setOpen(false); setText(""); }} data-testid={`myask-reply-cancel-${r.id}`} className="text-xs tracking-widest px-2 opacity-60 hover:opacity-100" style={{ color: "var(--text-secondary)" }}>CANCEL</button>
      </div>
    </div>
  );
}

function AskModal({ inquiry, onClose }) {
  const [responses, setResponses] = useState(null);
  const { viewProfile } = useProfileViewer();

  useEffect(() => {
    const load = () => api.get(`/inquiries/${inquiry.id}/responses`).then(({ data }) => setResponses(data));
    load();
    return startPoll(load, 5000);
  }, [inquiry.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in" style={{ background: "rgba(0,0,0,0.75)" }} onClick={onClose} data-testid="myask-modal-overlay">
      <div className="w-full max-w-xl p-8 max-h-[85vh] overflow-y-auto" style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }} onClick={(e) => e.stopPropagation()} data-testid="myask-modal">
        <div className="flex justify-between items-start mb-6">
          <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>your ask</span>
          <button onClick={onClose} data-testid="myask-modal-close" className="text-sm opacity-60 hover:opacity-100" style={{ color: "var(--text-primary)" }}>[ close ]</button>
        </div>
        <p className="long-text font-head font-light text-xl leading-relaxed pb-6 mb-6" style={{ color: "var(--text-primary)", borderBottom: "1px solid var(--border)" }}>{inquiry.text}</p>
        {responses === null ? (
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>
        ) : responses.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="myask-no-responses">No answers yet. Give it time.</p>
        ) : (
          <ul className="flex flex-col gap-6">
            {responses.map((r) => (
              <li key={r.id} data-testid={`myask-response-${r.id}`} className="p-5" style={{ border: "1px solid var(--border)" }}>
                <ExpandableText text={r.text} testid={`myask-response-text-${r.id}`} className="text-base leading-relaxed" style={{ color: "var(--text-primary)" }} />
                <button onClick={() => viewProfile(r.author?.id)} data-testid={`myask-response-author-${r.id}`} className="flex items-center gap-2 mt-4 opacity-70 hover:opacity-100 transition-opacity">
                  <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{r.author?.name || "unknown"}</span>
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>· {timeAgo(r.created_at)}</span>
                </button>
                <div className="mt-5 pt-5" style={{ borderTop: "1px solid var(--border)" }}><ResponseReply r={r} /></div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function MyAsks({ refreshKey }) {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const load = () => api.get("/inquiries/mine").then(({ data }) => setItems(data));
    load();
    return startPoll(load, 5000);
  }, [refreshKey]);

  const open = (inq) => {
    setSelected(inq);
    if (inq.has_response && !inq.answer_seen) {
      api.post(`/inquiries/${inq.id}/seen`).catch(() => {});
      setItems((prev) => prev.map((x) => (x.id === inq.id ? { ...x, answer_seen: true } : x)));
    }
  };

  return (
    <section className="w-full max-w-3xl mt-24 pb-16" data-testid="my-asks">
      <h2 className="text-xs tracking-widest mb-4 pb-2" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>my asks</h2>
      {items === null ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="my-asks-empty">You haven't asked anything yet.</p>
      ) : (
        <ul>
          {items.map((inq) => {
            const fresh = inq.has_response && !inq.answer_seen;
            return (
              <li key={inq.id} data-testid={`my-ask-${inq.id}`} onClick={() => open(inq)} className="py-5 px-4 cursor-pointer transition-colors duration-200 hover:bg-[var(--bg-secondary)]" style={{ borderBottom: "1px solid var(--border)", borderLeft: fresh ? "2px solid var(--accent)" : "2px solid transparent" }}>
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  {inq.subject && <span className="text-[10px] uppercase tracking-widest px-2 py-1" style={{ border: "1px solid var(--border)", color: "var(--accent)" }}>{inq.subject}</span>}
                  <span className="text-[10px] uppercase tracking-widest" style={{ color: inq.has_response ? "var(--accent)" : "var(--text-muted)" }} data-testid={`my-ask-status-${inq.id}`}>{inq.has_response ? (fresh ? "new answer" : "answered") : "waiting"}</span>
                </div>
                <p className="long-text font-head font-light text-lg leading-relaxed line-clamp-2" style={{ color: "var(--text-primary)" }}>{inq.text}</p>
                <span className="text-xs mt-2 block" style={{ color: "var(--text-muted)" }}>{timeAgo(inq.created_at)}</span>
              </li>
            );
          })}
        </ul>
      )}
      {selected && createPortal(<AskModal inquiry={selected} onClose={() => setSelected(null)} />, document.body)}
    </section>
  );
}
